import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { AlertOctagon, TrendingDown, TrendingUp, Shield, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number): string { return v >= 0 ? `$${v.toLocaleString()}` : `-$${Math.abs(v).toLocaleString()}`; }
const TOOLTIP_STYLE = { contentStyle: { backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }, itemStyle: { color: '#94a3b8' }, wrapperStyle: { outline: 'none' as const }, cursor: false };
const RISK_COLORS: Record<string, string> = { critical: '#ef4444', high: '#f97316', moderate: '#f59e0b', low: '#22c55e' };
const SEVERITY_COLORS: Record<string, string> = { critical: 'text-red-500', warning: 'text-yellow-500', watch: 'text-blue-500', healthy: 'text-green-500' };

export default function FailureWarningPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['failure-warning'],
    queryFn: async () => { const r = await apiClient.get('/failure-warning/scan'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const clients = (data?.clients || []).filter((c: any) => {
    if (filter !== 'all' && c.riskLevel !== filter) return false;
    if (search) return c.clientName.toLowerCase().includes(search.toLowerCase());
    return true;
  });

  const pieData = [
    { name: 'Critical', value: data?.critical || 0, fill: RISK_COLORS.critical },
    { name: 'High', value: data?.high || 0, fill: RISK_COLORS.high },
    { name: 'Moderate', value: data?.moderate || 0, fill: RISK_COLORS.moderate },
    { name: 'Low', value: data?.low || 0, fill: RISK_COLORS.low },
  ].filter(d => d.value > 0);

  const topRisk = (data?.clients || []).filter((c: any) => c.riskScore > 30).slice(0, 10).map((c: any) => ({
    name: c.clientName.length > 18 ? c.clientName.substring(0, 16) + '...' : c.clientName,
    score: c.riskScore,
    fill: RISK_COLORS[c.riskLevel] || '#6b7280',
  }));

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /><span className="ml-3 text-sm text-muted-foreground">Analyzing financial health of all clients...</span></div>;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={AlertOctagon}
        title="Business Failure Early Warning"
        description="Predictive scoring based on financial deterioration patterns, cash flow, and profitability trends"
        iconColor="bg-red-500/10 text-red-500"
      />


      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="pt-3 pb-3"><p className="text-[10px] text-muted-foreground uppercase">Clients Analyzed</p><p className="text-lg font-bold">{data?.totalClients || 0}</p></CardContent></Card>
        <Card className="border-red-500/30"><CardContent className="pt-3 pb-3"><p className="text-[10px] text-red-500 uppercase">Critical Risk</p><p className="text-lg font-bold text-red-500">{data?.critical || 0}</p></CardContent></Card>
        <Card className="border-orange-500/30"><CardContent className="pt-3 pb-3"><p className="text-[10px] text-orange-500 uppercase">High Risk</p><p className="text-lg font-bold text-orange-500">{data?.high || 0}</p></CardContent></Card>
        <Card className="border-yellow-500/30"><CardContent className="pt-3 pb-3"><p className="text-[10px] text-yellow-500 uppercase">Moderate</p><p className="text-lg font-bold text-yellow-500">{data?.moderate || 0}</p></CardContent></Card>
        <Card className="border-green-500/30"><CardContent className="pt-3 pb-3"><p className="text-[10px] text-green-500 uppercase">Low Risk</p><p className="text-lg font-bold text-green-500">{data?.low || 0}</p></CardContent></Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {pieData.length > 0 && <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Risk Distribution</CardTitle></CardHeader><CardContent>
          <ResponsiveContainer width="100%" height={200}><PieChart><Pie data={pieData} dataKey="value" nameKey="name" cx="35%" cy="50%" outerRadius={70} innerRadius={30} activeShape={null as any}>{pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}</Pie><Tooltip {...TOOLTIP_STYLE} cursor={false}/><Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} /></PieChart></ResponsiveContainer>
        </CardContent></Card>}
        {topRisk.length > 0 && <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Highest Risk Clients</CardTitle></CardHeader><CardContent>
          <ResponsiveContainer width="100%" height={200}><BarChart data={topRisk} layout="vertical"><CartesianGrid strokeDasharray="3 3" opacity={0.15} /><XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#888' }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#999' }} width={110} /><Tooltip {...TOOLTIP_STYLE} cursor={false}/><Bar dataKey="score" name="Risk Score" radius={[0, 4, 4, 0]} activeBar={false}>{topRisk.map((d: any, i: number) => <Cell key={i} fill={d.fill} />)}</Bar></BarChart></ResponsiveContainer>
        </CardContent></Card>}
      </div>

      {/* Client Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">All Clients - Business Health Assessment</CardTitle>
            <div className="flex gap-2">
              <div className="flex gap-1">
                {['all', 'critical', 'high', 'moderate', 'low'].map(f => (
                  <button key={f} onClick={() => setFilter(f)} className={cn('px-2 py-0.5 text-[10px] rounded-md font-medium capitalize', filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80')}>{f}</button>
                ))}
              </div>
              <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="w-40 h-6 text-xs" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {clients.map((c: any, i: number) => (
              <div key={i} className="flex items-start gap-3 p-3 hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/panel/intelligence/failure-warning/${c.clientId}`, { state: { client: c } })}>
                <div className="flex-shrink-0 mt-1">
                  <div className={cn('h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white', c.riskLevel === 'critical' ? 'bg-red-500' : c.riskLevel === 'high' ? 'bg-orange-500' : c.riskLevel === 'moderate' ? 'bg-yellow-500' : 'bg-green-500')}>{c.riskScore}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold">{c.clientName}</span>
                    <Badge variant="outline" className="text-[9px] capitalize">{c.entityType}</Badge>
                    <Badge style={{ backgroundColor: RISK_COLORS[c.riskLevel] + '20', color: RISK_COLORS[c.riskLevel], border: 'none' }} className="text-[9px] uppercase">{c.riskLevel}</Badge>
                  </div>
                  <div className="flex gap-4 text-[10px] text-muted-foreground mb-1">
                    <span>Revenue: {fmtCurrency(c.currentRevenue)} ({c.revenueChange >= 0 ? '+' : ''}{c.revenueChange}%)</span>
                    <span>Profit: {fmtCurrency(c.currentProfit)} ({c.profitMargin}% margin)</span>
                  </div>
                  {c.indicators?.length > 0 && (
                    <div className="space-y-0.5">
                      {c.indicators.slice(0, 3).map((ind: any, j: number) => (
                        <div key={j} className="flex items-start gap-1.5 text-[10px]">
                          <span className={cn('font-medium', SEVERITY_COLORS[ind.severity])}>{ind.indicator}:</span>
                          <span className="text-muted-foreground">{ind.detail.substring(0, 100)}{ind.detail.length > 100 ? '...' : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
